import React, { useState, useEffect, useRef } from "react";
import { captureSubmissionError } from "../../utils/sentryUtils";
import SubmittingCard from "../../components/SubmittingCard";
import SubmissionErrorCard from "../../components/SubmissionErrorCard";

const BrokerQuoteSubmission = ({
  transactionType,
  channel,
  broker,
  additionalInformation,
  personalDetails,
  sellingDetails,
  buyingDetails,
  referral,
  googleAnalyticsId,
  campaign,
  onFlowComplete,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref guard prevents StrictMode double-mount from firing a second request
  const hasInitiated = useRef(false);

  useEffect(() => {
    if (hasInitiated.current) return;
    hasInitiated.current = true;

    if (!personalDetails) {
      console.error("Broker quote submission blocked: personalDetails is null");
      captureSubmissionError("BrokerQuote", 0, "personalDetails was null at submission time");
      setError("Unable to generate your quote");
      setLoading(false);
      return;
    }

    const handleError = (err) => {
      console.error("Broker quote submission failed:", err);
      setError("Unable to generate your quote");
      setLoading(false);
    };

    fetch("/api/quote", {
      method: "PUT",
      body: JSON.stringify({
        transactionType,
        channel,
        agentName: broker.fullName,
        agentEmailAddress: broker.emailAddress,
        additionalInformation,
        personalDetails,
        sellingDetails: transactionType === "Selling" || transactionType === "BuyingAndSelling" ? sellingDetails : null,
        buyingDetails: transactionType === "Buying" || transactionType === "BuyingAndSelling" ? buyingDetails : null,
        remortgagingDetails: null,
        referrer: null,
        referralCode: referral.referralCode,
        googleAnalyticsId,
        campaign: campaign ?? null,
      }),
    })
      .then(async (resp) => {
        if (resp.ok) {
          const quoteData = await resp.json();
          setLoading(false);
          onFlowComplete({
            quoteDetails: quoteData,
          });
        } else {
          const body = await resp.text().catch(() => "");
          console.error("Broker quote submission failed:", resp.status, body);
          captureSubmissionError("BrokerQuote", resp.status, body);
          setError("Unable to generate your quote");
          setLoading(false);
        }
      }, handleError)
      // Catches errors thrown inside the .then() handlers (e.g. resp.json() SyntaxError)
      .catch(handleError);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- submit once on mount; ref guard prevents StrictMode re-fire

  if (loading) {
    return <SubmittingCard title="Working out your price" text="This won’t take long." />;
  }

  if (error) {
    return <SubmissionErrorCard error={error} />;
  }

  return null;
};

export default BrokerQuoteSubmission;
